const formatCompact = n =>
  n >= 1e9 ? (n / 1e9).toFixed(1) + "B" :
    n >= 1e6 ? (n / 1e6).toFixed(1) + "M" :
      n >= 1e3 ? (n / 1e3).toFixed(1) + "K" :
        Number(n).toLocaleString();

/**
 * Zero-pads a numeric timestamp component so the display keeps a fixed width.
 *
 * @param {string|number} value Numeric component to pad.
 * @param {number} [size=2] Target width.
 * @returns {string} Zero-padded component.
 */
function padTimestampPart(value, size = 2) {
  return String(value).padStart(size, '0');
}

/**
 * Safely calls a spinner method if the spinner is available on the current page.
 *
 * This abstracts away direct references to the spinner API, allowing pages to
 * use spinners when available without requiring them. If the spinner or the
 * specified method is not present, this function does nothing.
 *
 * @param {string} method Name of the spinner method to call (e.g., "loading", "loaded").
 * @param {...any} args Arguments to pass to the spinner method.
 */
export function safeSpinner(method, ...args) {
  const spinner = globalThis.$?.spinner || window.spinner;
  const handler = spinner?.[method];
  if (typeof handler === 'function') {
    handler.apply(spinner, args);
  }
}

/**
 * Normalizes a timestamp-like string so JavaScript can parse it consistently.
 *
 * The database emits bare timestamps without an explicit timezone. For values
 * that look like `YYYY-MM-DD HH:MM:SS[.sss]`, we treat them as UTC so the
 * browser can safely convert them to the viewer's local timezone.
 *
 * @param {string|Date|number|null|undefined} timestamp Raw timestamp value.
 * @returns {string|null} ISO-friendly timestamp string or `null` when empty.
 */
// Treat bare DB timestamps as UTC so the browser can shift them into the viewer's timezone.
function normalizeTimestampInput(timestamp) {
  if (timestamp === null || typeof timestamp === 'undefined' || timestamp === '') {
    return null;
  }

  var value = String(timestamp).trim();
  if (!value) {
    return null;
  }

  // DB timestamps are stored without timezone information, so mark them as UTC.
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value)) {
    return value.replace(' ', 'T') + 'Z';
  }

  return value.replace(' ', 'T');
}

/**
 * Formats a job timestamp for display in the browser's local timezone.
 *
 * This keeps the date/time layout stable while letting the browser apply the
 * viewer's locale and timezone offset. Callers can optionally suppress seconds
 * or include milliseconds when the source timestamp contains them.
 *
 * @param {string|Date|number|null|undefined} timestamp Raw timestamp value.
 * @param {{includeSeconds?: boolean, includeMilliseconds?: boolean}} [options]
 * Display options for the rendered string.
 * @returns {string} Local-time formatted timestamp, or an empty string when unset.
 */
// Format job timestamps in the browser's local timezone while keeping a stable display layout.
export function formatLocalTimestamp(timestamp, options = {}) {
  var normalizedTimestamp = normalizeTimestampInput(timestamp);
  if (!normalizedTimestamp) {
    return '';
  }

  // Let the browser convert the timestamp to the user's local timezone.
  var date = new Date(normalizedTimestamp);
  if (isNaN(date.getTime())) {
    return String(timestamp);
  }

  // Keep the display shape consistent across the app, regardless of locale.
  var includeSeconds = options.includeSeconds !== false;
  var includeMilliseconds = options.includeMilliseconds === true;
  var formatted = [
    date.getFullYear(), '-',
    padTimestampPart(date.getMonth() + 1), '-',
    padTimestampPart(date.getDate()), ' ',
    padTimestampPart(date.getHours()), ':',
    padTimestampPart(date.getMinutes())
  ];

  if (includeSeconds) {
    formatted.push(':', padTimestampPart(date.getSeconds()));
  }

  if (includeMilliseconds) {
    formatted.push('.', padTimestampPart(date.getMilliseconds(), 3));
  }

  return formatted.join('');
}

// Expose the formatter for legacy inline scripts that do not import modules.
window.formatLocalTimestamp = formatLocalTimestamp;

/**
 * Parses a job timestamp into epoch milliseconds in a NaN-safe way.
 *
 * Sorting and filtering must always operate on this raw value, never on a
 * display string. Unparseable input maps to 0 so comparisons stay stable.
 *
 * @param {string|Date|number|null|undefined} timestamp Raw timestamp value.
 * @returns {number} Epoch milliseconds, or 0 when the input cannot be parsed.
 */
export function parseJobTimestamp(timestamp) {
  var normalizedTimestamp = normalizeTimestampInput(timestamp);
  if (!normalizedTimestamp) {
    return 0;
  }

  var time = new Date(normalizedTimestamp).getTime();
  return isNaN(time) ? 0 : time;
}

/**
 * Formats a job timestamp compactly for activity listings.
 *
 * Users can submit several jobs per day, so the time of day is always shown;
 * the date part shrinks the closer the timestamp is to now:
 *   today          -> "Today 14:02"
 *   yesterday      -> "Yesterday 09:41"
 *   same year      -> "Aug 11, 09:41"
 *   older          -> "Dec 3 2025, 09:12"
 *
 * @param {string|Date|number|null|undefined} timestamp Raw timestamp value.
 * @returns {string} Local-time display string, or '' when unset/unparseable.
 */
export function formatActivityTimestamp(timestamp) {
  var time = parseJobTimestamp(timestamp);
  if (!time) {
    return '';
  }

  var date = new Date(time);
  var now = new Date();
  var hhmm = padTimestampPart(date.getHours()) + ':' + padTimestampPart(date.getMinutes());

  var sameDay = function (a, b) {
    return a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
  };

  if (sameDay(date, now)) {
    return 'Today ' + hhmm;
  }

  var yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (sameDay(date, yesterday)) {
    return 'Yesterday ' + hhmm;
  }

  var month = date.toLocaleString(undefined, { month: 'short' });
  if (date.getFullYear() === now.getFullYear()) {
    return month + ' ' + date.getDate() + ', ' + hhmm;
  }

  return month + ' ' + date.getDate() + ' ' + date.getFullYear() + ', ' + hhmm;
}

window.parseJobTimestamp = parseJobTimestamp;
window.formatActivityTimestamp = formatActivityTimestamp;

export function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function getJQuery() {
  return window.jQuery || window.$ || null;
}

const replayableReadyEvents = new Set(["jquery-ready", "d3-ready", "globals-ready"]);
const firedReadyEvents = new Set();
let readyEventReplayInstalled = false;

function scheduleReadyListener(callback, eventName) {
  const event = new Event(eventName);

  if (typeof callback === "function") {
    callback.call(document, event);
    return;
  }

  if (callback && typeof callback.handleEvent === "function") {
    callback.handleEvent(event);
  }
}

function installReadyEventReplay() {
  if (readyEventReplayInstalled) {
    return;
  }

  readyEventReplayInstalled = true;
  const originalAddEventListener = document.addEventListener.bind(document);

  document.addEventListener = function (type, listener, options) {
    if (replayableReadyEvents.has(type) && firedReadyEvents.has(type)) {
      queueMicrotask(() => scheduleReadyListener(listener, type));
      return;
    }

    return originalAddEventListener(type, listener, options);
  };
}

export function fireReadyEvent(eventName) {
  if (!replayableReadyEvents.has(eventName)) {
    document.dispatchEvent(new Event(eventName));
    return;
  }

  firedReadyEvents.add(eventName);
  installReadyEventReplay();
  document.dispatchEvent(new Event(eventName));
}

export function bootstrapOnJQueryReady(init) {
  if (typeof init !== "function") {
    return;
  }

  let started = false;

  const run = () => {
    if (started) {
      return;
    }

    if (document.readyState === "loading") {
      return;
    }

    if (!getJQuery()) {
      return;
    }

    started = true;
    init();
  };

  if (document.readyState === "loading") {
    document.addEventListener("globals-ready", run, { once: true });
  }

  document.addEventListener("jquery-ready", run, { once: true });
  run();
}

/**
 * Initializes popovers using whichever API is available in the current page.
 * Supports Bootstrap 4 jQuery plugin style and Bootstrap 5 class style.
 *
 * @param {string|Element|ArrayLike<Element>} selector CSS selector or element(s) to initialize.
 * @param {Object} options Popover options.
 * @returns {number} Number of matched elements.
 */
export function initPopoverCompat(selector, options) {
  var elements = null;
  var jq = window.jQuery || window.$;

  if (typeof selector === "string") {
    elements = document.querySelectorAll(selector);
  } else if (selector instanceof Element) {
    elements = [selector];
  } else if (selector && typeof selector.length === "number") {
    elements = selector;
  } else {
    return 0;
  }

  if (!elements || elements.length === 0) {
    return 0;
  }

  if (jq && jq.fn && typeof jq.fn.popover === "function") {
    if (typeof selector === "string") {
      if (typeof options === "undefined") {
        jq(selector).popover();
      } else {
        jq(selector).popover(options);
      }
    } else if (typeof options === "undefined") {
      jq(elements).popover();
    } else {
      jq(elements).popover(options);
    }
    return elements.length;
  }

  if (window.bootstrap && typeof window.bootstrap.Popover === "function") {
    var popoverOptions = options || {};
    Array.prototype.forEach.call(elements, function (elem) {
      if (typeof window.bootstrap.Popover.getOrCreateInstance === "function") {
        window.bootstrap.Popover.getOrCreateInstance(elem, popoverOptions);
      } else {
        new window.bootstrap.Popover(elem, popoverOptions);
      }
    });
  }

  return elements.length;
}

window.initPopoverCompat = initPopoverCompat;

export function beautify(variable, sentence_case = false) {
  //uppercase beginning of sentence
  var new_phrase = variable.replace(
    /^(?<char1>[a-z])/gm,
    function (match, char1) {
      return `${char1.toUpperCase()}`;
    }
  );
  //uppercase LIN and LINgroup(s)
  new_phrase = new_phrase.replace(
    /\s(?<prefix>lin)(?<postfix>group)?s?\s/gm,
    function (match, prefix, postfix) {
      return ` ${prefix.toUpperCase()}${postfix} `;
    }
  );

  //replace _ with spaces and correct sentence case
  new_phrase = new_phrase.replace(
    /_(?<word_char1>[a-z])?/gm,
    function (match, word_char1) {
      return sentence_case ? ` ${word_char1}` : ` ${word_char1.toUpperCase()}`;
    }
  );
  //replace camelCase with spaces and correct sentence case
  new_phrase = new_phrase.replace(
    /(?<prefix>[a-z]+)(?<postfix>[A-Z]+)/gm,
    function (match, prefix, postfix) {
      return sentence_case
        ? `${prefix} ${postfix.toLowerCase()}`
        : `${prefix} ${postfix}`;
    }
  );

  return new_phrase;
}
export function beautify_sentence(sentence, sentence_case = true) {
  if (!sentence || sentence == "") return "";
  const HIGH_LEVEL_RANKS = [
    "kingdom",
    "superkingdom",
    "phylum",
    "class",
    "order",
    "family",
    "genus",
    "species",
  ];
  //uppercase beginning of sentence
  var new_phrase = sentence.replace(
    /^(?<char1>[a-z])/gm,
    function (match, char1) {
      return `${char1.toUpperCase()}`;
    }
  );
  //uppercase LIN and LINgroup(s)
  new_phrase = new_phrase.replace(
    /\s(?<prefix>lin)(?<postfix>group)?s?\s/gm,
    function (match, prefix, postfix) {
      return ` ${prefix.toUpperCase()}${postfix} `;
    }
  );
  // uppercase start of genus name
  HIGH_LEVEL_RANKS.forEach(function (rankname) {
    let re = new RegExp(
      String.raw`\s(?<prefix>${rankname})\s(?<postfix>[a-z])`,
      "gm"
    );
    new_phrase = new_phrase.replace(re, function (match, prefix, postfix) {
      // negative example "This Species Consists of"
      // return ` ${prefix[0].toUpperCase()}${prefix.slice(1)} ${postfix.toUpperCase()}`;
      return ` ${prefix[0].toUpperCase()}${prefix.slice(1)} ${postfix}`;
    });
  });
  if (!sentence_case) return new_phrase;

  //uppercase beginning of each consecutive sentence
  new_phrase = new_phrase.replace(
    /\.\s(?<word_char1>[a-z])/gm,
    function (match, word_char1) {
      return `. ${word_char1.toUpperCase()}`;
    }
  );
  return new_phrase;
}

export function jobErrorHandler(xhr, status, error, edit = false) {
  if (!xhr.responseJSON) {
    var e =
      "Something complicated happened! did not get a good error message to show." +
      xhr.responseText;
  } else {
    var res = xhr.responseJSON;
    var e = res.status + " " + res.status_desc + ": " + res.message;
  }
  if (edit) $("#edit-status").attr("class", "text-danger").text(e);
  else if (typeof safeSpinner === 'function') safeSpinner('error', e);
  else alert(e);
  throw new Error(e);
}

export function setData(element, data) {
  for (let key in data) {
    element.dataset[key] = data[key];
  }
}


/**
 * Claims the spinner on behalf of the page, suppressing *implicit* hides.
 *
 * A page that is waiting on something of its own (the result page polling a job) must be
 * able to keep the spinner up while unrelated render paths finish. Those paths signal
 * "I rendered something, the spinner is probably stale" by calling sectionLoaded(), which
 * has no way of knowing a job is still in flight. A hold makes sectionLoaded() a no-op.
 *
 * Explicit statements by the owning page, spinner.loaded() and spinner.error(), are never
 * gated: the holder stays responsible for taking the spinner down itself.
 *
 * The counter lives on `window` rather than in module scope because the classic inline
 * script in templates/loading.php cannot import a module and has to read the same state.
 * It is deliberately not hung off `window.spinner`, which only exists after globals-ready.
 */
export function holdSpinner() {
  window.__grxivSpinnerHolds = (window.__grxivSpinnerHolds || 0) + 1;
}

/**
 * Drops one claim taken by holdSpinner().
 *
 * This never hides anything. Releasing on an error path must not tear down the message
 * spinner.error() just painted, so hiding stays the caller's explicit decision.
 */
export function releaseSpinner() {
  window.__grxivSpinnerHolds = Math.max(0, (window.__grxivSpinnerHolds || 0) - 1);
}

/** @returns {boolean} Whether any caller currently holds the spinner. */
export function spinnerIsHeld() {
  return (window.__grxivSpinnerHolds || 0) > 0;
}

export function sectionLoaded() {
  // An opportunistic hide, so it yields to anyone holding the spinner. Without this the
  // result page lost its animation seconds after load: initAppState() -> renderAllUI()
  // ends in sectionLoaded(), which fired regardless of whether the job was still running.
  if (spinnerIsHeld()) return;
  // Optional: pages that don't include templates/loading.php have no spinner to hide, and
  // this is now called from render paths that run on those pages too.
  const wrapper = document.querySelector('#spinner-wrapper');
  if (wrapper) wrapper.style.display = 'none';
}

/**
 * Normalize API autocomplete responses into objects consumed by jQuery UI.
 * Supports both tuple responses [label, count] and plain string values.
 *
 * @param {Array} results Raw autocomplete response data.
 * @returns {Array<{label: string, value: string, count: (number|null)}>} Normalized entries.
 */
export function format_autocomplete_results(results) {
  return results.map(function (item) {
    if (Array.isArray(item)) {
      return {
        label: item[0], // + "<span class='ms-2 badge rounded-pill count-badge bg-primary'>" + item[1] + "</span>",
        value: item[0],
        count: item[1]
      };
    } else {
      return { label: item, value: item, count: null };
    }
  });
}

/**
 * Returns page-scoped autocomplete state.
 * Keeps a single in-flight request and a short-lived in-memory cache
 * shared by all autocomplete inputs on the same page.
 *
 * @returns {{activeRequest: JQuery.jqXHR|null, cache: Map<string, Array>}}
 */
export function get_autocomplete_page_state() {
  if (!window.__grxivAutocompleteState) {
    window.__grxivAutocompleteState = {
      activeRequest: null,
      cache: new Map(),
    };
  }

  return window.__grxivAutocompleteState;
}

/**
 * Sorts and filters `extra` query parameters so cache keys and payloads are stable.
 * Empty/null values are omitted.
 *
 * @param {Object} extra Additional autocomplete filters.
 * @returns {Object} Normalized filter object.
 */
export function normalize_autocomplete_extra(extra) {
  if (!extra || typeof extra !== "object") return {};

  return Object.keys(extra)
    .sort()
    .reduce(function (acc, key) {
      const value = extra[key];
      if (value !== null && value !== undefined && value !== "") {
        acc[key] = value;
      }
      return acc;
    }, {});
}

/**
 * Escapes HTML special characters in suggestion labels.
 *
 * @param {string} text Raw text.
 * @returns {string} Escaped text safe for HTML insertion.
 */
export function escape_html(text) {
  // split/join instead of replaceAll (missing in Safari <13.1); "&" must stay first.
  return String(text ?? "")
    .split("&").join("&amp;")
    .split("<").join("&lt;")
    .split(">").join("&gt;")
    .split('"').join("&quot;")
    .split("'").join("&#39;");
}

/**
 * Escapes a string so it can be safely used in a RegExp pattern.
 *
 * @param {string} text Raw text.
 * @returns {string} Regex-escaped text.
 */
export function escape_regex(text) {
  return String(text).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Highlights the active search term inside a suggestion label.
 *
 * @param {string} label Suggestion label.
 * @param {string} term Current autocomplete term.
 * @returns {string} HTML string with highlighted matches.
 */
export function highlight_autocomplete_term(label, term) {
  const safeLabel = escape_html(label);
  const normalizedTerm = String(term || "").trim();
  if (!normalizedTerm) return safeLabel;

  const termRegex = new RegExp(`(${escape_regex(normalizedTerm)})`, "ig");
  return safeLabel.replace(termRegex, '<mark class="ac-hit">$1</mark>');
}

/**
 * Initializes API-backed autocomplete with sensible defaults.
 *
 * Defaults:
 * - `url`: `/api/autocomplete`
 * - `minLength`: `3`
 * - `delay`: `300`
 * - `useCountRenderer`: `true`
 *
 * @param {JQuery|HTMLElement|string} input Target input element.
 * @param {Object} [options] Autocomplete options.
 * @param {string} options.col Database column name (required).
 * @param {string} options.tab Database table name (required).
 * @param {string} [options.url="/api/autocomplete"] API endpoint.
 * @param {number} [options.minLength=3] Minimum characters before querying.
 * @param {number} [options.delay=300] Debounce delay (ms).
 * @param {boolean} [options.useCountRenderer=true] Enable count-aware item renderer.
 * @param {Object|Function} [options.extra={}] Static or dynamic extra filters.
 * @param {number|null} [options.limit=null] Optional API limit.
 */
let inlineSuggestionHoldInstalled = false;

/**
 * Keeps an in-flow suggestion list's container from collapsing mid-click.
 *
 * jQuery UI closes the list on the input's blur, which the browser fires at pointerdown,
 * before the click that caused it completes. An in-flow list that collapses at that
 * moment pulls whatever sits under it (the search forms' Search button) out from under
 * the pointer, so pointerup lands elsewhere and the click never happens: the button
 * looked dead, exactly what the floating list used to cause by covering it. pointerdown
 * precedes the focus change, so freezing the open containers' height here holds the
 * layout still until the pointer is released. Installed once per page.
 */
function install_inline_suggestion_hold() {
  if (inlineSuggestionHoldInstalled) return;
  inlineSuggestionHoldInstalled = true;

  const held = new Set();
  const release = () => {
    held.forEach((container) => {
      container.style.minHeight = "";
    });
    held.clear();
  };

  document.addEventListener(
    "pointerdown",
    () => {
      document.querySelectorAll(".ac-inline > .ui-autocomplete").forEach((menu) => {
        if (getComputedStyle(menu).display === "none") return;
        const container = menu.parentElement;
        container.style.minHeight = `${container.offsetHeight}px`;
        held.add(container);
      });
    },
    true
  );
  document.addEventListener("pointerup", release, true);
  document.addEventListener("pointercancel", release, true);
}

export function setup_api_autocomplete(input, {
  col,
  tab,
  url = "/api/autocomplete",
  minLength = 3,
  delay = 300,
  useCountRenderer = true,
  extra = {},
  limit = null,
  appendTo = null,
} = {}) {
  const $input = input && input.jquery ? input : $(input);
  if (!$input || !$input.length) return;

  // With `appendTo`, the suggestion list renders inside that container as part of
  // the page flow (see `.ac-inline` in grxivCommon.css) instead of floating over
  // whatever sits under the input. The search forms need this: their Search button
  // is right below the keyword box, and a floating list of three or more rows covered
  // it, so a click on "Search" picked a suggestion instead of submitting.
  const $appendTo = appendTo ? (appendTo.jquery ? appendTo : $(appendTo)) : null;
  if ($appendTo && $appendTo.length) {
    $appendTo.addClass("ac-inline");
    install_inline_suggestion_hold();
  }

  const column = col;
  const table = tab;
  if (!column || !table) {
    throw new Error("setup_api_autocomplete requires both `col` and `tab` options.");
  }

  const endpoint = url;

  const initializeAutocomplete = function () {
    if ($input.data("ui-autocomplete")) {
      $input.autocomplete("destroy");
    }

    $input.autocomplete({
      minLength,
      delay,
      appendTo: $appendTo && $appendTo.length ? $appendTo : null,
      source: function (request, response) {
        // Gather and normalize parameters for consistent caching and API requests.
        const state = get_autocomplete_page_state();
        // `extra` can be either a static object or a function(term) that returns filters.
        const resolvedExtra = typeof extra === "function"
          ? extra(request.term)
          : extra;
        // Keep a distinct name to avoid shadowing the outer `extra` option (TDZ hazard).
        const normalizedExtra = normalize_autocomplete_extra(resolvedExtra);

        const payload = {
          term: request.term,
          col: column,
          tab: table,
        };
        if (Object.keys(normalizedExtra).length) {
          payload.extra = normalizedExtra;
        }
        if (limit != null) {
          payload.limit = limit;
        }

        const cacheKey = JSON.stringify({
          term: String(request.term || "").toLowerCase(),
          col: payload.col,
          tab: payload.tab,
          extra: normalizedExtra,
          limit,
        });

        // Reuse cached entries for identical requests within the same page.
        if (state.cache.has(cacheKey)) {
          response(state.cache.get(cacheKey));
          return;
        }

        // Enforce a single in-flight request across autocomplete inputs on this page.
        if (state.activeRequest && state.activeRequest.readyState !== 4) {
          state.activeRequest.abort();
        }

        const xhr = $.ajax({
          url: endpoint,
          dataType: "json",
          data: payload,
        });

        state.activeRequest = xhr;

        xhr.done(function (data) {
          const formattedData = format_autocomplete_results(data);
          state.cache.set(cacheKey, formattedData);
          response(formattedData);
        });

        xhr.fail(function (_xhr, textStatus) {
          if (textStatus !== "abort") {
            response([]);
          }
        });

        xhr.always(function () {
          if (state.activeRequest === xhr) {
            state.activeRequest = null;
          }
        });
      },
    });

    if (useCountRenderer && $input.data("ui-autocomplete")) {
      $input.data("ui-autocomplete")._renderItem = autocomplete_count_renderer;
    }
  };

  const jqueryUiReady = window.__grxivJqueryUiReady;
  if (jqueryUiReady && typeof jqueryUiReady.then === "function") {
    jqueryUiReady.then(function (isReady) {
      if (isReady) {
        initializeAutocomplete();
      }
    });
    return;
  }

  initializeAutocomplete();
}



// Override the default item renderer
/**
 * Custom autocomplete row renderer that displays matched term + compact count badge.
 *
 * @param {JQuery} ul jQuery UI menu list element.
 * @param {{label: string, count: (number|null)}} item Suggestion item.
 * @returns {JQuery} Rendered list item.
 */
export function autocomplete_count_renderer(ul, item) {
  const highlightedLabel = highlight_autocomplete_term(item.label, this.term);
  const countHtml = (item.count != null)
    ? `<span class="ms-1 badge rounded-pill count-badge bg-primary">${formatCompact(item.count)}</span>`
    : "";

  return $("<li>")
    .append(
      `<div class="ac-row">
           <span class="ac-term">${highlightedLabel}</span>
           ${countHtml}
         </div>`
    )
    .appendTo(ul);


}


// Named exports are provided inline on each function; no aggregated export block needed.

if (typeof window !== "undefined") {
  window.setup_api_autocomplete = setup_api_autocomplete;
  window.jobErrorHandler = jobErrorHandler;
  window.initPopoverCompat = initPopoverCompat;
}
